import { computed } from 'vue'
import L from 'leaflet'
import { translateCropName } from '../i18n/cropVocab'
import i18n from '../i18n'

const t = (key, params) => i18n.global.t(`useMapLayers.${key}`, params)

// ⚠️ NON usare L.marker(latlng) senza un'icona esplicita.
// Il marker default di Leaflet carica marker-icon.png/marker-shadow.png via path relativo
// che Vite non risolve correttamente nel build di produzione → icona rotta (404).
// Usare sempre L.marker(latlng, { icon: L.divIcon({...}) }) con SVG inline,
// come fatto nel layer farmCenter qui sotto.
// Bug introdotto in commit 62df7e8 (Copilot, 12/05/2026) e corretto subito dopo.

function _safeZoomMarker(marker) {
  const orig = marker._animateZoom
  if (orig && !orig._safe) {
    marker._animateZoom = function (e) {
      if (!this._map) return
      orig.call(this, e)
    }
    marker._animateZoom._safe = true
  }
  return marker
}

/**
 * Base layers configuration for the map
 */
export const baseLayers = {
  Satellite: {
    name: 'Satellite',
    type: 'tile',
    url: 'https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
    options: {
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      attribution: '&copy; Google',
      maxZoom: 22,
      maxNativeZoom: 22
    },
    default: true
  },
  OpenStreetMap: {
    name: 'OpenStreetMap',
    type: 'tile',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 22,
      maxNativeZoom: 19
    }
  }
}

/**
 * Composable per generare i layers della mappa con colori basati sulle colture e tipi geo_element
 * @param {Object} params - Parametri per generare i layers
 * @param {Ref} params.farmCenter - Farm center object
 * @param {Ref} params.plotsWithFields - Array of plots with fields (from getPlotFields)
 * @param {Ref} params.geoElements - Array of geo_element objects
 * @param {Ref} params.geoElementTypes - Array of geo_element_type objects
 * @param {Function} params.getPlotTypeLabel - Function to get plot type label
 * @returns {Object} - Object containing mapLayers and legend computed
 */
export function useMapLayers({ farmCenter, plotsWithFields, geoElements, geoElementTypes, getPlotTypeLabel, hoveredPlotId }) {
  // Registry leaflet layer per id_plot — popolato lazily su primo hover mappa
  const plotLayerRefs = new Map()
  // Genera una legenda con le colture uniche e i tipi di geo_element
  const legend = computed(() => {
    const items = []
    
    // Sezione Colture
    const cropMap = new Map()
    const emptyColor = '#999999' // Grigio per plot senza colture
    
    plotsWithFields.value.forEach(plot => {
      if (plot.fields && plot.fields.length > 0) {
        plot.fields.forEach(field => {
          if (field.crop) {
            const cropId = field.crop.id_crop
            if (!cropMap.has(cropId)) {
              cropMap.set(cropId, {
                id: cropId,
                name: translateCropName(field.crop.name || field.crop.crop_name),
                color: field.crop.color || generateColorFromId(cropId),
                category: t('cropsCategory')
              })
            }
          }
        })
      }
    })

    items.push(...Array.from(cropMap.values()))

    // Aggiungi "Nessuna coltura" se ci sono plot vuoti
    const hasEmptyPlots = plotsWithFields.value.some(p => !p.fields || p.fields.length === 0)
    if (hasEmptyPlots) {
      items.push({
        id: 'empty',
        name: t('noCrop'),
        color: emptyColor,
        category: t('cropsCategory')
      })
    }
    
    
    
    return items
  })


  const legendGeoElements = computed(() => {
    const items = []
    const typeMap = new Map()
    
    if (geoElementTypes.value) {
      geoElementTypes.value.forEach(type => {
        const typeId = type.id_geo_element_type
        if (!typeMap.has(typeId)) {
          typeMap.set(typeId, {
            id: typeId,
            name: type.name || type.type_name || t('typeFallback', { id: typeId }),
            color: generateGeoElementColor(typeId),
            category: t('geoElementsCategory')
          })
        }
      })
    }
    
    items.push(...Array.from(typeMap.values()))
    
    return items
  })


  const mapLayers = computed(() => {
    const layers = []

    // Add farm center marker as GeoJSON point layer
    if (farmCenter.value?.lat && (farmCenter.value?.lon || farmCenter.value?.lng)) {
      layers.push({
        name: t('farmCenter'),
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [farmCenter.value.lon || farmCenter.value.lng, farmCenter.value.lat]
          },
          properties: {
            name: farmCenter.value.name
          }
        },
        options: {
          keep_original: true,
          // L.marker() con icona default Leaflet rompe in prod (Vite non risolve i path
          // delle immagini leaflet/dist/images/marker-icon.png dopo il build).
          // divIcon SVG inline: nessuna dipendenza da asset esterni.
          pointToLayer: (_feature, latlng) => _safeZoomMarker(L.marker(latlng, {
            icon: L.divIcon({
              className: '',
              html: `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
                <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="#2A81CB" stroke="#1a6aad" stroke-width="1"/>
                <circle cx="12.5" cy="12.5" r="4.5" fill="white"/>
              </svg>`,
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34]
            })
          })),
          popup: true,
          popupContent: `<strong>${farmCenter.value.name}</strong>`
        }
      })
    }

    // Add plots/fields as GeoJSON layer with crop-based colors
    // Se un field ha geometria custom, crea una feature separata per quel field
    // Altrimenti usa la geometria del plot
    const plotFeatures = []
    
    plotsWithFields.value.forEach(plot => {
      // Se il plot ha fields con geometrie custom, crea feature separate
      const fieldsWithCustomGeometry = plot.fields?.filter(f => f.geojson) || []
      
      if (fieldsWithCustomGeometry.length > 0) {
        // Crea una feature per ogni field con geometria custom
        fieldsWithCustomGeometry.forEach(field => {
          let geometry = null
          if (field.geojson) {
            const g = typeof field.geojson === 'string' ? JSON.parse(field.geojson) : field.geojson
            geometry = g.type === 'Feature' ? g.geometry : g
          }
          if (!geometry) return
          
          const color = field.crop?.color || generateColorFromId(field.crop?.id_crop)
          const cropInfo = translateCropName(field.crop?.name || field.crop?.crop_name) || t('noCrop')
          
          plotFeatures.push({
            type: 'Feature',
            properties: {
              name: field.name || plot.name,
              plotName: plot.name,
              area: field.surface || plot.surface || plot.area,
              type: plot.plot_type,
              color: color,
              cropInfo: cropInfo,
              isCustomGeometry: true
            },
            geometry
          })
        })
        
        // Se ci sono anche fields senza geometria custom, usa la geometria del plot per loro
        const fieldsWithoutCustomGeometry = plot.fields?.filter(f => !f.geojson) || []
        if (fieldsWithoutCustomGeometry.length > 0 && plot.geojson) {
          let geometry = null
          const g = typeof plot.geojson === 'string' ? JSON.parse(plot.geojson) : plot.geojson
          geometry = g.type === 'Feature' ? g.geometry : g
          
          if (geometry) {
            const firstField = fieldsWithoutCustomGeometry[0]
            const color = firstField.crop?.color || generateColorFromId(firstField.crop?.id_crop)
            const crops = fieldsWithoutCustomGeometry.map(f => translateCropName(f.crop?.name || f.crop?.crop_name)).filter(Boolean)
            const cropInfo = crops.length > 0 ? crops.join(', ') : t('noCrop')
            
            plotFeatures.push({
              type: 'Feature',
              properties: {
                name: plot.name,
                area: plot.surface || plot.area,
                type: plot.plot_type,
                color: color,
                cropInfo: cropInfo,
                fieldsCount: fieldsWithoutCustomGeometry.length
              },
              geometry
            })
          }
        }
      } else {
        // Nessun field ha geometria custom, usa la geometria del plot
        let geometry = null
        if (plot.geojson) {
          const g = typeof plot.geojson === 'string' ? JSON.parse(plot.geojson) : plot.geojson
          geometry = g.type === 'Feature' ? g.geometry : g
        } else if (plot.polygon_coordinates?.length > 0) {
          geometry = {
            type: 'Polygon',
            coordinates: [plot.polygon_coordinates.map(([lat, lon]) => [lon, lat])]
          }
        }
        if (!geometry) return
        
        // Determina il colore in base alla coltura
        let color = '#999999' // Grigio default per plot senza colture
        let cropInfo = t('noCrop')
        let id_field= null
        
        if (plot.fields && plot.fields.length > 0) {
          const firstField = plot.fields[0]
          if (firstField.crop) {
            color = firstField.crop.color || generateColorFromId(firstField.crop.id_crop)
            cropInfo = translateCropName(firstField.crop.name || firstField.crop.crop_name)
            id_field = firstField.id_field
          }

          if (plot.fields.length > 1) {
            const crops = plot.fields.map(f => translateCropName(f.crop?.name || f.crop?.crop_name)).filter(Boolean)
            cropInfo = crops.join(', ')
          }
        }
        
        plotFeatures.push({
          type: 'Feature',
          properties: {
            name: plot.name,
            area: plot.surface || plot.area,
            type: plot.plot_type,
            color: color,
            cropInfo: cropInfo,
            fieldsCount: plot.fields?.length || 0,
            id_field: id_field,
            id_plot: plot.id_plot
          },
          geometry
        })
      }
    })

    if (plotFeatures.length > 0) {
      layers.push({
        name: t('fields'),
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: plotFeatures
        },
        options: {
          keep_original: true,
          style: function(feature) {
            return {
              fillColor: feature.properties.color,
              color: feature.properties.color,
              weight: 2,
              fillOpacity: 0.5,
              opacity: 1
            }
          },
          onFeatureReady: (feature, layer) => {
            if (feature.properties.id_plot) plotLayerRefs.set(feature.properties.id_plot, layer)
          },
          onMouseOver: (feature, layer) => {
            if (hoveredPlotId) hoveredPlotId.value = feature.properties.id_plot
            layer.setStyle({ fillOpacity: 0.85, weight: 4 })
          },
          onMouseOut: (feature, layer) => {
            if (hoveredPlotId) hoveredPlotId.value = null
            layer.setStyle({ fillOpacity: 0.5, weight: 2 })
          },
          popup: true,
          popupTemplate: (props) => {
            let content = `<strong>${props.name}</strong><br>`
            if (props.plotName && props.plotName !== props.name) {
              content += `<em>Plot: ${props.plotName}</em><br>`
            }
            content += `${t('area')}: ${(props.area || 0).toFixed(0)} m²<br>`
            content += `${t('type')}: ${getPlotTypeLabel(props.type)}<br>`
            content += `${t('crop')}: ${props.cropInfo}`
            if (props.fieldsCount > 0) {
              content += `<br>${t('fieldsCount')}: ${props.fieldsCount}`
            }
            if (props.isCustomGeometry) {
              content += `<br><em>(${t('customGeometry')})</em>`
            }
            if(props.id_field) {
              content += `<a href="#/poderi/${farmCenter.value.id_farm_center}/field/${props.id_field}" style="display:block;margin-top:5px;color:blue;text-decoration:underline;">${t('goToField')}</a>`
            }
            return content
          }
        }
      })
    }

    // Add geo_elements as GeoJSON layer with type-based colors
    if (geoElements?.value && geoElements.value.length > 0) {
      const geoElementFeatures = geoElements.value
        .map(element => {
          let geometry = null
          if (element.geojson) {
            const g = typeof element.geojson === 'string' ? JSON.parse(element.geojson) : element.geojson
            geometry = g.type === 'Feature' ? g.geometry : g
          }
          if (!geometry) return null
          
          // Determina il colore in base al tipo (element.id_type è FK a geo_element_type.id_geo_element_type)
          const color = generateGeoElementColor(element.id_type)

          // Trova il nome del tipo
          let typeName = t('notAvailable')
          if (geoElementTypes?.value && element.id_type) {
            const geoType = geoElementTypes.value.find(
              (gt) => String(gt.id_geo_element_type) === String(element.id_type),
            );
            if (geoType) {
              typeName = geoType.name || geoType.label || geoType.type_name || t('typeFallback', { id: element.id_type })
            }
          }
          
          return {
            type: 'Feature',
            properties: {
              name: element.name,
              elementType: typeName,
              description: element.description || '',
              color: color,
              id_geo_element: element.id_geo_element
            },
            geometry
          }
        })
        .filter(f => f !== null)

      if (geoElementFeatures.length > 0) {
        layers.push({
          name: t('geoElements'),
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: geoElementFeatures
          },
          options: {
            keep_original: true,
            style: function(feature) {
              return {
                fillColor: feature.properties.color,
                color: feature.properties.color,
                weight: 2,
                fillOpacity: 0.4,
                opacity: 1,
                dashArray: '5, 5' // Linea tratteggiata per distinguere dagli appezzamenti
              }
            },
            popup: true,
            popupTemplate: (props) => {
              let html = `
                <strong>${props.name}</strong><br>
                ${t('type')}: ${props.elementType}
              `
              if (props.description) {
                html += `<br>${t('description')}: ${props.description}`
              }
              if (props.id_geo_element && farmCenter.value?.id_farm_center) {
                html += `<br><a href="#/poderi/${farmCenter.value.id_farm_center}/geo-element/${props.id_geo_element}" style="color:blue;text-decoration:underline;margin-top:5px;display:block;">${t('goToDetail')} →</a>`
              }
              return html
            }
          }
        })
      }
    }

    return layers
  })

  return {
    mapLayers,
    legend,
    legendGeoElements,
    baseLayers,
    plotLayerRefs
  };
}

/**
 * Genera un colore consistente da un ID numerico (per colture)
 */
function generateColorFromId(id) {
  const colors = [
    '#FFD700', '#FF6347', '#4CAF50', '#2196F3', '#FF9800',
    '#9C27B0', '#00BCD4', '#FFEB3B', '#795548', '#607D8B',
    '#E91E63', '#8BC34A', '#FFC107', '#673AB7', '#009688'
  ]
  return colors[id % colors.length]
}

/**
 * Genera un colore consistente per geo_element_type (tonalità diverse dai plot)
 */
function generateGeoElementColor(id) {
  const colors = [
    '#8B4513', '#A0522D', '#D2691E', '#CD853F', '#DEB887', // Tonalità marroni per edifici
    '#708090', '#778899', '#B0C4DE', '#4682B4', '#5F9EA0', // Tonalità grigio-blu per strutture
    '#556B2F', '#6B8E23', '#808000', '#BDB76B', '#9ACD32'  // Tonalità verde oliva per recinzioni
  ]
  return colors[id % colors.length]
}
